/**
 * Workspace Selection Page
 * 
 * Displayed after login when user has access to multiple workspaces.
 * Allows the user to choose which workspace to work in.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { Button, Card } from '../../components/ui';

const WorkspaceSelectionPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { 
    selectWorkspace, 
    availableWorkspaces, 
    isLoading, 
    error, 
    clearError,
    resetWorkspaceSelectionState,
    logout,
  } = useAuthStore();
  
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    
    if (!selectedWorkspaceId) return;
    
    const success = await selectWorkspace(selectedWorkspaceId);
    if (success) {
      navigate('/dashboard');
    }
  };

  const handleCancel = async () => {
    resetWorkspaceSelectionState();
    await logout();
    navigate('/login');
  };

  // Get workspace type icon
  const getWorkspaceIcon = (type: string) => {
    switch (type) {
      case 'personal':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case 'business':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'association':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
          </svg>
        );
    }
  };

  // Get role badge color
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'admin':
        return 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400';
      case 'member':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'viewer':
        return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
      default:
        return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
    }
  };

  return (
    <Card className="max-w-lg mx-auto">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-violet-100 dark:bg-violet-900/30 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
          {t('auth.workspaceSelection.title', 'Choisir un espace')}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          {t('auth.workspaceSelection.subtitle', 'Vous avez accès à plusieurs espaces. Sélectionnez celui sur lequel vous souhaitez travailler.')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {availableWorkspaces.map((workspace) => (
            <button
              key={workspace.id}
              type="button"
              onClick={() => setSelectedWorkspaceId(workspace.id)}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                selectedWorkspaceId === workspace.id
                  ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                  : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
              }`}
            >
              {/* Icon */}
              <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                selectedWorkspaceId === workspace.id
                  ? 'bg-violet-100 dark:bg-violet-800 text-violet-600 dark:text-violet-400'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
              }`}>
                {getWorkspaceIcon(workspace.type)}
              </div>

              {/* Workspace info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-medium truncate ${
                    selectedWorkspaceId === workspace.id
                      ? 'text-violet-700 dark:text-violet-300'
                      : 'text-slate-900 dark:text-white'
                  }`}>
                    {workspace.displayName || workspace.name}
                  </span>
                  {workspace.isDefault && (
                    <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                      {t('workspaces.default', 'Par défaut')}
                    </span>
                  )}
                  <span className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(workspace.role)}`}>
                    {t(`workspaces.role.${workspace.role}`, workspace.role)}
                  </span>
                </div>
                {workspace.ownerEmail && workspace.role !== 'owner' && (
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                    {t('auth.workspaceSelection.ownedBy', 'Propriétaire:')} {workspace.ownerEmail}
                  </p>
                )}
              </div>

              {/* Selection indicator */}
              <div className="flex-shrink-0">
                {selectedWorkspaceId === workspace.id ? (
                  <svg className="w-6 h-6 text-violet-600 dark:text-violet-400" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <div className="w-6 h-6 rounded-full border-2 border-slate-300 dark:border-slate-600" />
                )}
              </div>
            </button>
          ))}
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <Button 
            type="button" 
            variant="secondary" 
            className="flex-1"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button 
            type="submit" 
            className="flex-1" 
            isLoading={isLoading} 
            disabled={!selectedWorkspaceId}
          >
            {t('auth.workspaceSelection.continue', 'Continuer')}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default WorkspaceSelectionPage;
